import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Question, Topic } from "../Questions/api";
import { BookOpen } from "lucide-react";
import {
  customerStatusLabel,
  jurisdictionLabel,
  reviewApi,
  topicLabel,
} from "./api";
import { usePortal } from "../Portal/PortalContext";
import ProposalTerms from "./ProposalTerms";
import { formatPrice, useReviewPrice } from "./reviewPrice";
import "./proposals.css";
import ReviewHelp from "./ReviewHelp";
import QuestionIntakeEditor from "../Questions/QuestionIntakeEditor";
import { useRequestDraft } from "./useRequestDraft";
type OpenReview = NonNullable<Question["reviews"]>[number];

// Wait this long after typing stops before fetching the terms for the request.
export const TERMS_DELAY_MS = 600;

/** The latest review that is still running, if any; a question has one at a time. */
export function openReviewOf(question: Pick<Question, "reviews">) {
  return question.reviews?.find(
    r => r.status !== "cancelled" && r.status !== "completed" && !r.closed_at
  );
}

/**
 * The attorney-review offer shown under the AI answers, and the one-screen
 * request it opens: what to check, the exact terms for that request, and one
 * consent. While a review is running it links to that review instead.
 */
export default function SubmitReview({
  question,
  topics = [],
  onQuestionUpdated,
  hasUserMessages,
  isBusy,
  hasUnsavedDocuments,
  openReview,
  onOpenReview,
  onSubmitted,
  initialRequest,
  compact = false,
  autoOpen = false,
  onAutoOpenConsumed,
  reviewsReady = true,
  latestReviewId = null,
}: {
  question: Question;
  topics?: Topic[];
  onQuestionUpdated?: (question: Question) => void;
  hasUserMessages: boolean;
  isBusy: boolean;
  hasUnsavedDocuments: boolean;
  openReview?: OpenReview;
  onOpenReview?: () => void;
  onSubmitted?: () => void;
  initialRequest?: string;
  compact?: boolean;
  autoOpen?: boolean;
  reviewsReady?: boolean;
  latestReviewId?: string | null;
  onAutoOpenConsumed?: () => void;
}) {
  const access = usePortal().access;
  const reviewPrice = useReviewPrice();
  const charged = Boolean(access?.paymentsEnabled);
  const [editingIntake, setEditingIntake] = useState(false);
  const [show, setShow] = useState(false),
    [consent, setConsent] = useState(false),
    [saving, setSaving] = useState(false),
    [preparing, setPreparing] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [retry, setRetry] = useState(0),
    [submitted, setSubmitted] = useState(false),
    // The terms the server prepared, and the request text they were made for.
    [proposal, setProposal] = useState<any>(null);
  const betaMode = access?.targetedBeta === true;
  const [coverage, setCoverage] = useState<boolean | null>(null),
    [coverageError, setCoverageError] = useState("");
  useEffect(() => {
    if (!betaMode) {
      setCoverage(true);
      return;
    }
    const c = new AbortController();
    setCoverage(null);
    setCoverageError("");
    reviewApi(
      "/coverage",
      "POST",
      { topicKey: question.topicKey, jurisdiction: question.jurisdiction },
      c.signal
    )
      .then(d => {
        if (!c.signal.aborted) setCoverage(d.available);
      })
      .catch(e => {
        if (!c.signal.aborted) setCoverageError(e.message);
      });
    return () => c.abort();
  }, [betaMode, question.topicKey, question.jurisdiction, retry]);
  const [offerHidden, setOfferHidden] = useState(false);
  const draft = useRequestDraft(question.id, latestReviewId, reviewsReady);
  const reviewRequest = draft.body;
  const key = draft.submissionKey;
  const autoOpened = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const navigate = useNavigate();
  const blocked =
    !access ||
    (betaMode && coverage !== true) ||
    isBusy ||
    hasUnsavedDocuments ||
    !hasUserMessages ||
    !draft.ready ||
    submitted ||
    editingIntake;
  const request = reviewRequest.trim();
  const current = Boolean(
    proposal &&
      proposal.forRequest === request &&
      (!betaMode || proposal.forParties === draft.parties.trim()) &&
      proposal.forVersion === question.version
  );
  function openRequest() {
    draft.initialize(
      latestReviewId ? "" : initialRequest || question.questionText || ""
    );
    setProposal(null);
    setConsent(false);
    setError("");
    setNotice("");
    setShow(true);
  }

  useEffect(() => {
    if (!autoOpen) {
      autoOpened.current = false;
      return;
    }
    if (autoOpened.current || !draft.ready) return;
    if (openReview) {
      autoOpened.current = true;
      onAutoOpenConsumed?.();
      onOpenReview?.();
      return;
    }
    if (!hasUserMessages || isBusy) return;
    autoOpened.current = true;
    openRequest();
    onAutoOpenConsumed?.();
    // Consume the URL intent once, after the chat has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, hasUserMessages, isBusy, openReview, draft.ready]);

  useEffect(() => {
    setConsent(false);
    setProposal(null);
  }, [question.id, question.version]);

  useEffect(() => {
    setSubmitted(false);
  }, [latestReviewId, openReview?.id]);

  // Fetch the exact terms for what the customer typed, once they pause.
  useEffect(() => {
    if (!show || blocked || !request || (betaMode && !draft.parties.trim()))
      return;
    if (
      proposal?.forRequest === request &&
      (!betaMode || proposal.forParties === draft.parties.trim()) &&
      proposal.forVersion === question.version
    )
      return;
    let active = true;
    const timer = setTimeout(async () => {
      setPreparing(true);
      setError("");
      try {
        const result = await reviewApi(
          `/questions/${question.id}/proposal`,
          "POST",
          {
            version: question.version,
            reviewRequest: request,
            ...(betaMode ? { parties: draft.parties.trim() } : {}),
          }
        );
        if (active)
          setProposal({
            ...result,
            forRequest: request,
            forParties: draft.parties.trim(),
            forVersion: question.version,
          });
      } catch (e: any) {
        if (active) setError(e.message);
      } finally {
        if (active) setPreparing(false);
      }
    }, TERMS_DELAY_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    show,
    blocked,
    request,
    proposal,
    question.id,
    question.version,
    retry,
    betaMode,
    draft.parties,
  ]);

  async function submit() {
    if (blocked || !consent || saving || !current) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const r = await reviewApi(`/questions/${question.id}/submit`, "POST", {
        submissionKey: key.current,
        version: question.version,
        savedDocumentsConfirmed: true,
        reviewRequest: request,
        proposalAccepted: true,
        proposalHash: proposal.proposalHash,
        ...(proposal.notice
          ? {
              parties: draft.parties.trim(),
              betaAcceptance: {
                accepted: true,
                version: proposal.notice.version,
                hash: proposal.notice.hash,
              },
            }
          : {}),
        ...(charged ? { chargeAuthorized: true } : {}),
      });
      if (mounted.current) {
        setSubmitted(true);
        setShow(false);
      }
      draft.clear();
      if (!mounted.current) return;
      if (charged) {
        // Save the card now so it can be charged the moment an attorney accepts.
        // If this fails, the review page offers to add the card later.
        try {
          const { url } = await reviewApi<{ url: string }>(
            `/${r.id}/payment/card`,
            "POST",
            {}
          );
          if (mounted.current) window.location.assign(url);
          return;
        } catch {
          /* fall through to the review page */
        }
      }
      if (!mounted.current) return;
      onSubmitted?.();
      navigate(`/questions/${question.id}?tab=attorney`);
    } catch (e: any) {
      if (!mounted.current) return;
      setError(e.message);
      if (e.status === 409) {
        // The terms or materials changed: fetch fresh terms and ask again.
        onSubmitted?.();
        setNotice(e.message);
        setError("");
        draft.change(reviewRequest);
        setProposal(null);
        setConsent(false);
      } else if (!e.status || e.status >= 500) {
        setNotice(
          "We couldn’t confirm whether your request was received. Retry the same request or check My questions; retrying it won’t create a duplicate."
        );
        onSubmitted?.();
      }
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  if (openReview)
    return (
      <section
        className={`rv-review-offer is-open${compact ? " is-inline" : ""}`}
        aria-label="Attorney review"
      >
        <div>
          <strong>Attorney review · {customerStatusLabel(openReview)}</strong>
          <span>
            The attorney sees this chat as it was when you asked. Message them
            in the review.
          </span>
        </div>
        <Button variant="outline-dark" size="sm" onClick={onOpenReview}>
          View review
        </Button>
      </section>
    );
  // Offer a review once there is an AI answer to check.
  if (!access) return <p role="status">Loading review options…</p>;
  if (betaMode && coverage !== true && !openReview && !show)
    return (
      <section className="rv-review-offer">
        <p>
          {coverageError ||
            (coverage === false
              ? "Attorney review is not available for this topic/location or no participating reviewer is available in the beta."
              : "Checking review availability…")}
        </p>
        {coverageError && (
          <Button onClick={() => setRetry(n => n + 1)}>
            Retry availability
          </Button>
        )}
      </section>
    );
  if (!hasUserMessages || (isBusy && !show) || !draft.ready) return null;
  if (submitted)
    return <p role="status">Request received. Opening your review…</p>;
  const fileCount = proposal?.materials
    ? proposal.materials.documents.length + proposal.materials.files.length
    : 0;
  return (
    <>
      <section
        className={`rv-review-offer${betaMode ? " is-beta" : compact ? " is-inline" : ""}`}
        aria-label="Attorney review offer"
      >
        <div>
          <strong>Attorney review?</strong>
          {reviewPrice && (
            <span>
              {formatPrice(reviewPrice.priceCents)}{" "}
              {betaMode ? "simulated fee · no charge" : ""} · answered within{" "}
              {reviewPrice.turnaroundHours} hours after the agreed review starts
            </span>
          )}
        </div>
        {betaMode ? (
          offerHidden ? (
            <button
              className="btn btn-link"
              onClick={() => setOfferHidden(false)}
            >
              Show review options
            </button>
          ) : (
            <div className="beta-review-intent">
              <Button
                onClick={openRequest}
                disabled={
                  !access || (betaMode && coverage !== true) || isBusy || saving
                }
              >
                Yes — see terms
              </Button>
              <Form.Control
                aria-label="Review instruction"
                value={draft.body}
                maxLength={4000}
                placeholder="Please verify this answer."
                onChange={e => draft.change(e.target.value)}
              />
              <Button variant="light" onClick={() => setOfferHidden(true)}>
                Not now
              </Button>
            </div>
          )
        ) : (
          <>
            <Button
              size="sm"
              className="rv-request-review"
              onClick={openRequest}
              disabled={
                !access || (betaMode && coverage !== true) || isBusy || saving
              }
            >
              <BookOpen size={15} aria-hidden="true" />
              <span>
                Get an attorney review
                {compact && reviewPrice
                  ? ` · ${formatPrice(reviewPrice.priceCents)}`
                  : ""}
              </span>
            </Button>
          </>
        )}
      </section>
      <Modal
        show={show}
        onHide={() => !saving && setShow(false)}
        size="lg"
        centered
        scrollable
        className="review-request-modal"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Get an attorney review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="request-intro">
            Check this summary and tell the attorney what you need to decide.
            You’ll receive a written assessment with corrections or limitations
            and recommended next steps. Drafting or rewriting a contract or
            clause is not included.
          </p>
          <p className="request-intake-summary">
            {topicLabel(question.topicKey)} ·{" "}
            {jurisdictionLabel(question.jurisdiction)}
            {onQuestionUpdated && topics.length > 0 && !editingIntake && (
              <Button
                variant="link"
                disabled={saving}
                onClick={() => {
                  setEditingIntake(true);
                }}
              >
                Edit topic and location
              </Button>
            )}
          </p>
          {editingIntake && (
            <QuestionIntakeEditor
              question={question}
              topics={topics}
              onReloaded={q => onQuestionUpdated?.(q)}
              onSaved={q => {
                onQuestionUpdated?.(q);
                setNotice(
                  "Details updated. Review the refreshed terms before submitting."
                );
                setEditingIntake(false);
                setProposal(null);
                setConsent(false);
              }}
              onCancel={() => setEditingIntake(false)}
            />
          )}
          <p className="request-pilot-note">
            Matching time varies, with no fixed matching deadline. The answer
            clock starts after matching, once the agreed review starts.
          </p>
          {betaMode && coverage !== true && (
            <Alert variant="info">
              {coverageError ||
                (coverage === false
                  ? "No review coverage is available for these details. Edit the topic/location or contact support."
                  : "Checking review availability…")}
            </Alert>
          )}
          <ReviewHelp />
          {reviewPrice && (
            <p className="request-price-line">
              {betaMode && <span>Simulated fee — no charge · </span>}
              <strong>{formatPrice(reviewPrice.priceCents)}</strong> · answered
              within {reviewPrice.turnaroundHours} hours after the agreed review
              starts
            </p>
          )}
          {notice && <Alert variant="info">{notice}</Alert>}
          {error && (
            <Alert className="mt-3" variant="danger">
              {error}
              <Button
                variant="link"
                disabled={preparing || saving}
                onClick={() => {
                  setProposal(null);
                  setConsent(false);
                  setRetry(n => n + 1);
                }}
              >
                Retry terms
              </Button>
            </Alert>
          )}
          {!hasUserMessages && (
            <Alert variant="info">
              Ask the AI a question first and wait for its answer.
            </Alert>
          )}
          {isBusy && (
            <Alert variant="info">Wait for the AI or upload to finish.</Alert>
          )}
          {hasUnsavedDocuments && (
            <Alert variant="info">
              Save your document changes before submitting.
            </Alert>
          )}
          <Form.Group className="mt-3" controlId="review-request-question">
            <Form.Label>What should the attorney check?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              required
              maxLength={4000}
              value={reviewRequest}
              disabled={saving}
              placeholder={
                latestReviewId
                  ? "What changed since your last review, and what should the attorney assess now?"
                  : "For example: Please assess the ownership clause and explain its risks before I sign."
              }
              aria-describedby="review-request-help"
              onChange={e => {
                draft.change(e.target.value);
                // Consent covers the terms for this exact request.
                setConsent(false);
              }}
            />
            <Form.Text id="review-request-help">
              Include the decision you’re facing. The attorney sees this
              question’s chat and files along with your request.
            </Form.Text>
          </Form.Group>
          {betaMode && (
            <Form.Group className="mt-3" controlId="review-parties">
              <Form.Label>People or companies involved</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={2000}
                value={draft.parties}
                disabled={saving}
                placeholder="Include yourself and the other parties, or enter 'none' for a generic example."
                onChange={e => {
                  draft.changeParties(e.target.value);
                  setConsent(false);
                }}
              />
              <Form.Text>
                Used by staff and the assigned attorney for conflict checks. Do
                not put these names in support emails.
              </Form.Text>
            </Form.Group>
          )}
          <p className="small text-muted" role="status">
            {draft.saved
              ? "Request draft saved in this browser tab."
              : "Your request draft could not be saved. Keep this page open until you submit or copy your text."}
          </p>
          {current ? (
            <section
              className="proposal-preview mt-3"
              aria-label="Your review terms"
            >
              <ProposalTerms
                terms={proposal.terms}
                compact
                request={request}
                charged={charged}
              />
              {proposal.materials && (
                <section
                  className="submission-manifest"
                  aria-label="Included materials"
                >
                  <h3>What the attorney sees</h3>
                  <p>
                    {proposal.materials.messageCount} messages · {fileCount}{" "}
                    file
                    {fileCount === 1 ? "" : "s"}
                  </p>
                  {fileCount > 0 && (
                    <details>
                      <summary>Show files</summary>
                      <ul>
                        {proposal.materials.documents.map((d: any) => (
                          <li key={d.id}>
                            {d.name} · version {d.version} · Saved
                          </li>
                        ))}
                        {proposal.materials.files.map((f: any, i: number) => (
                          <li key={i}>{f.name} · captured file</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {!fileCount && <p>Your chat only. No files.</p>}
                  <small>
                    We save a copy of this AI chat and its files when you
                    submit. Later AI-chat messages are not added. Messages you
                    send in the attorney review are shared directly with your
                    attorney.
                  </small>
                </section>
              )}
            </section>
          ) : (
            request &&
            !blocked && (
              <p className="request-pilot-note" role="status">
                {preparing || !error ? "Preparing the terms…" : null}
              </p>
            )
          )}
          {proposal?.notice && (
            <details className="mt-3">
              <summary>Read the test-review notice</summary>
              <p>{proposal.notice.text}</p>
              <small>{proposal.notice.version}</small>
            </details>
          )}
          <Form.Check
            id="review-consent"
            className="mt-3"
            label={
              charged
                ? `I approve the price, timing, and what’s included. ${access?.chargeAuthorization || ""}`
                : proposal?.notice
                  ? "I accept the test-review notice, fixed fee, scope, and timing. No charge will be made."
                  : "I approve the fixed fee, timing, and what’s included."
            }
            checked={consent}
            disabled={blocked || saving || !current}
            onChange={e => {
              setConsent(e.target.checked);
              if (e.target.checked) setNotice("");
            }}
          />
          <p className="request-pilot-note">
            {charged
              ? "Next, save a card securely with Stripe. Saving it does not charge you. The agreed price is charged when an attorney accepts, or when you approve revised terms. Reviewed is operated by Norix Legal LLC, the merchant shown at checkout."
              : "Beta test — no charge will be made."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={saving}
            onClick={() => setShow(false)}
          >
            Keep working
          </Button>
          <Button
            disabled={blocked || saving || !current || !consent}
            onClick={submit}
          >
            {saving
              ? "Submitting…"
              : charged
                ? "Continue to card details"
                : "Submit request"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
